import { mountInfoAskPill } from './shared/components/InfoAskPill';
import { mountPhotoQueuePill } from './shared/components/PhotoQueuePill';   // R114 fix: inspection photos still on this phone, on every page
import { TopNav } from './shared/components/TopNav';
import { Sidebar } from './shared/components/Sidebar';
import { TriagePage } from './pages/TriagePage';
import { UsersPage } from './pages/UsersPage';
import { OwnerPortalPage } from './pages/OwnerPortalPage';
import { MoneyTracking } from './pages/MoneyTracking';
import { ProjectionsPage } from './pages/ProjectionsPage';
import { ListingsPage } from './pages/ListingsPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { SettingsPage } from './pages/SettingsPage';
import { ProfitabilityPage } from './pages/ProfitabilityPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { OperationsPage } from './pages/OperationsPage';
import { GapNightsPage } from './pages/GapNightsPage';
import { EventPricingPage } from './pages/EventPricingPage';
import { BookingQuotePage } from './pages/BookingQuotePage';
import { DataExplorerPage } from './pages/DataExplorerPage';
import { AdminPage } from './pages/AdminPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { StatementsPage } from './pages/StatementsPage';
import { SupExpPage } from './pages/SupExpPage';
import { MtrPage } from './pages/MtrPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { InspectionsPage } from './pages/InspectionsPage';
import { HousekeepingPage } from './pages/HousekeepingPage';
import { StrikesPage } from './pages/StrikesPage';
import { ListingOptimizerPage } from './pages/ListingOptimizerPage';
import { IssuesPage } from './pages/IssuesPage';
import { ClaimsPage } from './pages/ClaimsPage';
import { GuestRecoveryPage } from './pages/GuestRecoveryPage';
import { QuickLogPage } from './pages/QuickLogPage';
import { EodPage } from './pages/EodPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SuppliesPage } from './pages/SuppliesPage';
import { ImpactPage } from './pages/ImpactPage';
import { ReportsPage } from './pages/ReportsPage';
import { GlossaryPage } from './pages/GlossaryPage';
import { OwnersPage } from './pages/OwnersPage';
import { OwnerProfilePage } from './pages/OwnerProfilePage';
import { SopsPage } from './pages/SopsPage';
import { JarvisPage } from './pages/JarvisPage';
import { FfePage } from './pages/FfePage';
import { HubPage } from './pages/HubPage';
import { PortfolioPage } from './pages/PortfolioPage';
import { ExecSummaryPage } from './pages/ExecSummaryPage';
import { TasksPage } from './pages/TasksPage';
import { ResTrackerPage } from './pages/ResTrackerPage';
import { ResolutionsPage } from './pages/ResolutionsPage';
import { QuoInboxPage } from './pages/QuoInboxPage';
import { StaysCalendarPage } from './pages/StaysCalendarPage';   // R110 #1: Calendar (stays timeline)
import { serializeFocus, FOCUS_KEY } from '../../lib/calendarCore.mjs';   // R115: ?open=stay:<id> → the Calendar's deep link
import { PropertySettingsPage } from './pages/PropertySettingsPage';
import { VacanciesPage } from './pages/VacanciesPage';
import { TeamPage } from './pages/TeamPage';
import { OpsBoardPage } from './pages/OpsBoardPage';
import { initInfoLayer } from './shared/components/InfoIcon';
import { InternalExpensesPage } from './pages/InternalExpensesPage';

type PageName = 'admin' | 'onboarding' | 'statements' | 'mtr' | 'maintenance' | 'inspections' | 'housekeeping' | 'issues' | 'analytics' | 'supplies' | 'users' | 'owner-portal' | 'today' | 'triage' | 'money' | 'gap-nights' | 'event-pricing' | 'projections' | 'profitability' | 'portfolio' | 'listings' | 'reservations' | 'settings' | 'reviews' | 'operations' | 'booking-quote' | 'data-explorer' | 'impact' | 'reports' | 'glossary' | 'owners' | 'sops' | 'jarvis' | 'ffe' | 'field-ops' | 'owner-ops' | 'tasks' | 'arrivals' | 'guest-texts' | 'hk-insp' | 'pnl' | 'field-log' | 'eod' | 'owner-profile' | 'supexp' | 'accounting' | 'resolutions' | 'internal-expenses' | 'special-offers' | 'stays' | 'guest-recovery' | 'claims' | 'tasks-hub' | 'remote-ops' | 'messaging';

export class App {
  private appContainer: HTMLElement;
  private nav: TopNav;
  private sidebar: Sidebar;
  private currentPage: PageName = 'today';
  private contentContainer!: HTMLElement;
  private pageInstances: Map<PageName, any> = new Map();
  private lastRefreshTime: Date = new Date();
  private _autoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private _relativeTimeTimer: ReturnType<typeof setInterval> | null = null;

  constructor(container: HTMLElement) {
    this.appContainer = container;
    this.nav = new TopNav(this.navigate.bind(this));
    this.sidebar = new Sidebar(this.navigate.bind(this));
    console.log('App constructor called');
  }

  async render(): Promise<void> {
    console.log('App render started');

    const currentMonth = new Date().toISOString().slice(0, 7);

    this.appContainer.innerHTML = `
      <div id="sidebar-overlay" class="sidebar-overlay"></div>

      <!-- Global Search Overlay -->
      <div id="search-overlay" style="display:none; position:fixed; inset:0; background:rgba(15,23,42,0.45); z-index:9000; align-items:flex-start; justify-content:center; padding-top:80px;">
        <div style="background:var(--card); border-radius:var(--radius-lg); box-shadow:var(--shadow-pop); border:1px solid var(--border); width:100%; max-width:580px; overflow:hidden;">
          <div style="display:flex; align-items:center; gap:10px; padding:14px 18px; border-bottom:1px solid var(--border);">
            <span style="font-size:18px; color:var(--text-faint);">🔍</span>
            <input id="search-input" type="text" placeholder="Search listings, reservations, tickets…" autocomplete="off"
              style="flex:1; border:none; outline:none; font-size:16px; color:var(--text); background:transparent;">
            <kbd style="font-size:11px; color:var(--text-faint); border:1px solid var(--border); border-radius:6px; padding:2px 6px;">Esc</kbd>
          </div>
          <div id="search-results" style="max-height:400px; overflow-y:auto; padding:8px 0; min-height:60px;">
            <div style="padding:20px; text-align:center; color:var(--text-faint); font-size:13px;">Type to search…</div>
          </div>
        </div>
      </div>

      ${this.sidebar.render()}
      <div class="app-main">
        ${this.nav.render()}
        <main class="main-content">
          <div id="page-content"></div>
        </main>
      </div>
    `;

    this.contentContainer = document.getElementById('page-content')!;
    initInfoLayer();
    this.initPageInstances();
    this.nav.bindEvents();
    this.sidebar.bindEvents();
    this.bindGlobalEvents();
    // Land each role where their work is: owners on the portal, field techs
    // straight on their tool (so a phone login opens the job, not the CFO view),
    // back-office staff on Company Pulse.
    let role = 'admin';
    try { role = sessionStorage.getItem('userRole') || 'admin'; } catch (_) { /* */ }
    const HOME: Record<string, PageName> = {
      owner: 'owner-portal', inspector: 'inspections', maintenance: 'maintenance', cleaner: 'housekeeping',
      accounting: 'accounting',   // R100 A1: the external accounting partner lands on their workspace, never Analytics
    };
    // R109 (l): a Slack "Open" link lands on the item, not the role HOME.
    const deep = this.takeDeepLink(role);
    if (deep) {
      await this.navigate(deep);
      mountInfoAskPill();
      mountPhotoQueuePill();
      console.log('App render completed (deep link)');
      return;
    }
    // Office staff land on the Analytics hub (it opens on Company Pulse); field
    // roles land directly on their tool, owners on their portal.
    await this.loadPage(HOME[role] || 'analytics');
    mountInfoAskPill();   // R109 #b: questions the office sent "in the app"
    this.nav.mountBell();   // R115 C1: task notification bell — 60 s poll while visible (TopNav starts it on deep-link landings)
    mountPhotoQueuePill();   // R114 fix: "N photos on this phone waiting to upload · Review"
    console.log('App render completed');
  }

  // R109 (l): Slack links are APP_URL/?open=wo:<id> | task:<id> | issue:<boardId>.
  // main.ts stashed the param as 'deep_open' (it survives the login reload)
  // and scrubbed the URL; here it becomes the one-shot focus hand-off each
  // page already reads (mn_focus_wo / tk_focus_task / iss_focus).
  // Owners / the accounting partner, and cleaners on an issue link (no Issues
  // board for them), fall back to their HOME.
  // R111: Issues is a first-class page (the Tasks & Issues hub is gone), so an
  // issue link lands on 'issues' directly — no hub_tab — for every role that
  // has an Issues entry: the four office roles plus inspector / maintenance
  // (their My Work "Issues" row).
  private takeDeepLink(role: string): string | null {
    let raw = '';
    try { raw = sessionStorage.getItem('deep_open') || ''; sessionStorage.removeItem('deep_open'); } catch { return null; }
    const m = /^(wo|task|issue|stay):([^\r\n<>"'`]{1,200})$/.exec(raw);
    if (!m || role === 'owner' || role === 'accounting') return null;
    const [, kind, id] = m;
    try {
      if (kind === 'wo') { sessionStorage.setItem('mn_focus_wo', id); return 'maintenance'; }
      if (kind === 'task') { sessionStorage.setItem('tk_focus_task', id); return 'tasks'; }
      // R115: a stay link opens it on the Calendar (office roles only — the calendar is office-only); the id is validated by the focus schema
      if (kind === 'stay') {
        const f = ['admin', 'director', 'manager', 'remote_ops'].includes(role) ? serializeFocus({ stay_id: id, src: 'link' }) : null;
        if (!f) return null;
        sessionStorage.setItem(FOCUS_KEY, f);
        return 'stays';
      }
      if (kind === 'issue' && ['admin', 'director', 'manager', 'remote_ops', 'inspector', 'maintenance'].includes(role)) {
        sessionStorage.setItem('iss_focus', id);
        return 'issues';
      }
    } catch { /* private mode */ }
    return null;
  }

  private initPageInstances(): void {
    console.log('Initializing page instances');
    this.pageInstances.set('admin', new AdminPage(this.contentContainer));
    this.pageInstances.set('onboarding', new OnboardingPage(this.contentContainer));
    this.pageInstances.set('statements', new StatementsPage(this.contentContainer));
    // R90 (Jesse): Supplies & Expenses — its own tab; the statement-review
    // team (admin/director/manager, same widening as R85). The page gates
    // itself in render() so the singleton keeps the suspendAutoRefresh
    // contract (auto-refresh must not eat mid-typed search/quote drafts).
    this.pageInstances.set('supexp', new SupExpPage(this.contentContainer));
    this.pageInstances.set('mtr', new MtrPage(this.contentContainer));
    this.pageInstances.set('maintenance', new MaintenancePage(this.contentContainer));
    this.pageInstances.set('inspections', new InspectionsPage(this.contentContainer));
    this.pageInstances.set('housekeeping', new HousekeepingPage(this.contentContainer));
    this.pageInstances.set('issues', new IssuesPage(this.contentContainer));
    this.pageInstances.set('field-log', new QuickLogPage(this.contentContainer));
    this.pageInstances.set('eod', new EodPage(this.contentContainer));
    this.pageInstances.set('analytics', new AnalyticsPage(this.contentContainer));
    this.pageInstances.set('supplies', new SuppliesPage(this.contentContainer));
    this.pageInstances.set('triage', new TriagePage(this.contentContainer));
    this.pageInstances.set('users', new UsersPage(this.contentContainer));
    this.pageInstances.set('owner-portal', new OwnerPortalPage(this.contentContainer));
    this.pageInstances.set('gap-nights', new GapNightsPage(this.contentContainer));
    this.pageInstances.set('event-pricing', new EventPricingPage(this.contentContainer));
    this.pageInstances.set('listings', new ListingsPage(this.contentContainer));
    this.pageInstances.set('reservations', new ReservationsPage(this.contentContainer));
    this.pageInstances.set('settings', new SettingsPage(this.contentContainer));
    this.pageInstances.set('profitability', new ProfitabilityPage(this.contentContainer));
    // Retired routes: the Money hub dissolved in R17 — pace/market moved to
    // Analytics, P&L got its own tab. Old ids redirect so deep links keep working.
    const redirect = (target: string, tab?: string) => ({
      render: async () => {
        if (tab) { try { sessionStorage.setItem('hub_tab', tab); } catch { /* private mode */ } }
        await this.navigate(target);
      },
    });
    this.pageInstances.set('money', redirect('analytics', 'tracking') as any);
    this.pageInstances.set('projections', redirect('analytics', 'market') as any);
    this.pageInstances.set('field-ops', redirect('hk-insp') as any);
    this.pageInstances.set('reviews', new ReviewsPage(this.contentContainer));
    this.pageInstances.set('operations', new OperationsPage(this.contentContainer));
    this.pageInstances.set('booking-quote', new BookingQuotePage(this.contentContainer));
    this.pageInstances.set('data-explorer', new DataExplorerPage(this.contentContainer));
    this.pageInstances.set('impact', new ImpactPage(this.contentContainer));
    this.pageInstances.set('reports', new ReportsPage(this.contentContainer));
    this.pageInstances.set('glossary', new GlossaryPage(this.contentContainer));
    this.pageInstances.set('owners', new OwnersPage(this.contentContainer));
    this.pageInstances.set('owner-profile', new OwnerProfilePage(this.contentContainer));
    this.pageInstances.set('sops', new SopsPage(this.contentContainer));
    this.pageInstances.set('jarvis', new JarvisPage(this.contentContainer));
    this.pageInstances.set('ffe', new FfePage(this.contentContainer));
    this.pageInstances.set('tasks', new TasksPage(this.contentContainer));
    // R111 (Jesse): Guest Recovery is its own sidebar entry (was the 'recovery'
    // tab of the Tasks & Issues hub). One persistent instance — render() is
    // idempotent (innerHTML reset + fetch + draw) and the drafts live on the
    // instance, so App.autoRefresh reaches its suspendAutoRefresh directly.
    this.pageInstances.set('guest-recovery', new GuestRecoveryPage(this.contentContainer));
    this.pageInstances.set('arrivals', redirect('remote-ops', 'arrivals') as any);   // R105 #6: Arrivals lives in Remote Operations (deep links keep landing)
    // R54: Guest Texts is a first-class sidebar page (was a Remote Ops hub
    // tab). A persistent instance — the open thread survives navigation, and
    // App.autoRefresh reaches suspendAutoRefresh directly for the draft veto.
    this.pageInstances.set('guest-texts', new QuoInboxPage(this.contentContainer));
    this.pageInstances.set('messaging', redirect('guest-texts') as any);   // R112: the tab is "Messaging" now — the new id lands on the same persistent page
    this.pageInstances.set('stays', new StaysCalendarPage(this.contentContainer));   // R110 #1: Calendar — persistent so the window/selection survive navigation

    // Nav consolidation (2026-06-27): collapse the ~25 sidebar items into a few
    // HubPages — one sidebar entry holding related pages as internal tabs, so
    // nothing is lost. Each tab mounts a FRESH child-page instance into the host
    // (the standalone registrations above stay reachable via search / deep-link /
    // field-role landing). Money is already a hub (MoneyPage); these are the rest.
    // UI/UX V8 sweep: Analytics = read the portfolio (one overview, the
    // Revenue Manager workup promoted out of its buried table-click, health,
    // impact, reports). Company Pulse and Deep Dive retired — their data lives
    // on Portfolio Overview and the Issues board; Performance was absorbed
    // into Portfolio Overview.
    // R16: Listing Quality gets its own hub (Reviews + Listing Health); Arrivals
    // + the AI Revenue Manager are first-class sidebar entries (V8 3 — "easier
    // to find").
    // R111 (Jesse: "move the tasks board by itself, with its own tab. Issues
    // should be by themselves as well. Move damages & claims to remote
    // operations. Move Guest Recovery to its own tab"): the R16 Tasks & Issues
    // hub is dissolved — Tasks / Issues / Guest Recovery are the persistent
    // standalone instances registered above, Damages & Claims is a Remote
    // Operations tab below. 'tasks-hub' stays registered as a COMPATIBILITY
    // page: anything that still navigates here with a one-shot hub_tab (an
    // old bookmark, a stale call site) is forwarded to the new home of that
    // tab. redirect() can't do this because it never reads hub_tab. Written
    // inline with this.navigate(): currentPage is 'tasks-hub' while render()
    // runs, so the same-page no-op never bites (the redirect() helper relies
    // on exactly this). The key is ALWAYS consumed so a stale 'tasks' /
    // 'issues' / 'recovery' can't leak into the next hub that renders.
    this.pageInstances.set('tasks-hub', {
      render: async () => {
        let pre = '';
        try { pre = sessionStorage.getItem('hub_tab') || ''; sessionStorage.removeItem('hub_tab'); } catch { /* private mode */ }
        if (pre === 'claims') {
          try { sessionStorage.setItem('hub_tab', 'claims'); } catch { /* private mode */ }
          await this.navigate('remote-ops');
          return;
        }
        const target = pre === 'issues' ? 'issues' : pre === 'recovery' ? 'guest-recovery' : 'tasks';
        await this.navigate(target);
      },
    } as any);
    // Financial/sensitive tabs keep the admin/director gate wherever they live
    // (role access rule: financials + performance data are never visible to
    // managers/field).
    const finGate = (mount: (h: HTMLElement) => any) => (h: HTMLElement) => {
      const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
      if (role === 'admin' || role === 'director') return mount(h);
      h.innerHTML = `<div class="page-content" style="padding:48px; text-align:center; color:var(--text-muted);">🔒 Limited to Admin and Director roles.</div>`;
    };
    this.pageInstances.set('listing-quality', new HubPage(this.contentContainer,
      { icon: '⭐', title: 'Listing Quality', purpose: 'How guests experience the homes — reviews as they land, each listing\'s multi-factor health, and AI listing optimization.' },
      [
        { id: 'reviews', label: 'Reviews', mount: h => new ReviewsPage(h).render() },
        { id: 'health', label: 'Listing Health', mount: h => new TriagePage(h).render() },
        { id: 'optimizer', label: 'AI Optimizer', mount: finGate(h => new ListingOptimizerPage(h).render()) },
      ]));
    this.pageInstances.set('analytics', new HubPage(this.contentContainer,
      { icon: '📊', title: 'Analytics', purpose: 'Read the portfolio in one place — the overview, revenue pace vs projections, the market benchmark scenarios, open vacancies by week, pricing impact, and the printable reports.' },
      [
        { id: 'overview', label: 'Portfolio Overview', mount: h => new PortfolioPage(h).render() },
        // R87 #4: the leadership read. Second on purpose — finGate would lock
        // the hub's LANDING tab for managers if it sat first.
        { id: 'exec-summary', label: 'Executive Summary', mount: finGate(h => new ExecSummaryPage(h).render()) },
        { id: 'tracking', label: 'Booked · Projected · Actual', mount: finGate(h => new MoneyTracking(h).render()) },
        { id: 'market', label: 'Market & Fee Types', mount: finGate(h => new ProjectionsPage(h).render()) },
        { id: 'vacancies', label: 'Vacancies', mount: h => new VacanciesPage(h).render() },
        { id: 'impact', label: 'Impact', mount: h => new ImpactPage(h).render() },
        { id: 'reports', label: 'Reports', mount: h => new ReportsPage(h).render() },
      ]));
    this.pageInstances.set('portfolio', new HubPage(this.contentContainer,
      { icon: '🏠', title: 'Portfolio', purpose: 'The properties themselves — the roster, every per-property setting in one place, and the reservations that fill them.' },
      [
        { id: 'listings', label: 'Listings', mount: h => new ListingsPage(h).render() },
        { id: 'settings', label: 'Property Settings', mount: h => new PropertySettingsPage(h).render() },
        { id: 'reservations', label: 'Reservations', mount: h => new ReservationsPage(h).render() },
        // R81 #5 (Mark): blocked-nights audit — no-note blocks + sync warnings.
        { id: 'calendar-blocks', label: 'Calendar blocks', mount: async h => { const { CalendarBlocksPage } = await import('./pages/CalendarBlocksPage'); await new CalendarBlocksPage(h).render(); } },
      ]));
    // Remote Operations (V8 2D): the desk work — arrivals prep, the issues
    // board, guest reviews, mid-term rentals, and the resolutions ledger
    // (R46: opened to managers so the team can log resolutions — field roles
    // still never reach the hub; all OTHER financial surfaces stay finGated).
    // R16: Arrivals + Issues + Reviews moved to their own homes; the Team page
    // (staff schedules + contacts) joins the desk-side hub.
    // R105 #6 (Jesse): Arrivals joins the desk-side hub; Mid-Term Rentals is
    // its own sidebar entry; Resolutions lives in Accounting only (the
    // 'resolutions' page id already redirects there).
    // R111 (Jesse): Damages & Claims moves here from the dissolved Tasks &
    // Issues hub — same office-roles gate (R50: claims carry $ amounts +
    // Resolutions-ledger hooks; the server's CLAIM_ROLES is the enforcement).
    // The `roles` hint keeps Arrivals as the landing tab for everyone, so no
    // role lands on the 🔒 screen.
    this.pageInstances.set('remote-ops', new HubPage(this.contentContainer,
      { icon: '🌐', title: 'Remote Operations', purpose: 'The desk-side work — upcoming arrivals and reservation tracking, the team\'s schedules & contact info, and the damage claims ledger.' },
      [
        // return the instance → the hub delegates suspendAutoRefresh (an unsent check note / dirty template survives the 15-min timer)
        { id: 'arrivals', label: 'Arrivals', mount: async h => { const p = new ResTrackerPage(h); await p.render(); return p; } },
        { id: 'team', label: 'Team', mount: h => new TeamPage(h).render() },
        // returns the instance → an open claim form survives the 15-min timer once ClaimsPage grows suspendAutoRefresh (R111)
        { id: 'claims', label: 'Damages & Claims', roles: ['admin', 'director', 'manager', 'remote_ops'], mount: async h => {
          const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
          if (['admin', 'director', 'manager', 'remote_ops'].includes(role)) { const p = new ClaimsPage(h); await p.render(); return p; }
          h.innerHTML = `<div class="page-content" style="padding:48px; text-align:center; color:var(--text-muted);">🔒 Damages & Claims is limited to office roles.</div>`;
        } },
      ],
      // stale one-shot hub_tab values from before the move still land
      { mtr: { page: 'mtr' }, resolutions: { page: 'accounting', tab: 'resolutions' } }));
    // R111: deep-link id for the claims tab (search / app:navigate / old bookmarks) — lands on the hub with the tab preselected, like 'arrivals'.
    this.pageInstances.set('claims', redirect('remote-ops', 'claims') as any);
    this.pageInstances.set('owner-ops', new HubPage(this.contentContainer,
      { icon: '🤝', title: 'Owner Operations', purpose: 'Everything owner-facing in one place — the portal each owner sees, the monthly owner statements, and the owner roster behind it.' },
      [
        { id: 'portal', label: 'Owner Portal', mount: h => new OwnerPortalPage(h).render() },
        // R85 (Jesse: "I, the accounting lead, and the PMs can go in and put
        // comments and close these flags out") — managers join the statement
        // review; the server now gates the whole /api/statements router to
        // admin/director/manager, so this widening is enforced, not cosmetic.
        { id: 'statements', label: 'Owner Statements', mount: h => {
          const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
          if (role === 'admin' || role === 'director' || role === 'manager') return new StatementsPage(h).render();
          h.innerHTML = `<div class="page-content" style="padding:48px; text-align:center; color:var(--text-muted);">🔒 Limited to Admin, Director and Manager roles.</div>`;
        } },
        { id: 'owners', label: 'Owners', mount: h => new OwnersPage(h).render() },
        // R117: the unified owner inbox (portal chat + owners@ Gmail + the owner Quo line) and owner outreach (merge-field emails / texts + the survey)
        { id: 'inbox', label: 'Inbox', mount: async h => { const { OwnerInboxPage } = await import('./pages/ownerOps/OwnerInboxPage'); const p = new OwnerInboxPage(h); await p.render(); return p; } },
        { id: 'outreach', label: 'Outreach', mount: async h => { const { OwnerOutreachPage } = await import('./pages/ownerOps/OwnerOutreachPage'); const p = new OwnerOutreachPage(h); await p.render(); return p; } },
      ]));
    // R17: Field Ops split apart — HK & Inspections (with the supply chain)
    // together, Maintenance / Onboarding / SOPs each first-class.
    this.pageInstances.set('hk-insp', new HubPage(this.contentContainer,
      { icon: '🧹', title: 'Housekeeping & Inspections', purpose: 'The turn cycle in one place — housekeeping turns, quality inspections, and the supplies & housewares that keep homes stocked.' },
      [
        { id: 'ops-board', label: 'Ops board', mount: h => new OpsBoardPage(h).render() },
        // R37: return the instance so the hub can delegate suspendAutoRefresh —
        // these two pages host mid-task field surfaces (capture, open jobs).
        { id: 'housekeeping', label: 'Housekeeping', mount: async h => { const p = new HousekeepingPage(h); await p.render(); return p; } },
        { id: 'inspections', label: 'Inspections', mount: async h => { const p = new InspectionsPage(h); await p.render(); return p; } },
        { id: 'supplies', label: 'Supplies', mount: h => new SuppliesPage(h).render() },
        { id: 'ffe', label: 'FF&E', mount: h => new FfePage(h).render() },
        { id: 'performance', label: 'Performance', mount: finGate(h => new StrikesPage(h).render()) },
      ]));
    this.pageInstances.set('pnl', new HubPage(this.contentContainer,
      { icon: '💰', title: 'P&L Review', purpose: 'Property-level profit & loss, and the formulas & fee definitions the numbers are built from.' },
      [
        { id: 'pnl', label: 'P&L', mount: finGate(h => new ProfitabilityPage(h).render()) },
        // R118 (Jesse: "I do not see any area(s) to setup the gross revenue
        // formulas that I can assign to properties."): the gross formulas, their
        // assignment, the channel rates, "Match the statements" and the formula
        // vs statement compare all live in Formulas & Fees now (the R117
        // 'Gross revenue rules' tab is gone). Returns the instance → the hub
        // delegates suspendAutoRefresh (an open modal / unsaved rates / a dry
        // run waiting for Apply survive the 15-min timer).
        { id: 'formulas', label: 'Formulas & Fees', mount: finGate(async h => { const p = new SettingsPage(h, { slim: true }); await p.render(); return p; }) },
      ]));
    // R100 A2 (Jesse: "we really need to create an Accounting main tab and
    // organize all of this"): supplies & expenses, resolutions, the special-
    // offer breakdowns, the internal expenses (fees) sheet and the statement
    // reviewer under one entry. Each tab's mount gate mirrors its server gate
    // exactly; the external accounting partner reaches all of them and
    // nothing else (its API access is deny-by-default server-side).
    const roleGate = (allowed: string[], label: string, mount: (h: HTMLElement) => any) => (h: HTMLElement) => {
      const role = (() => { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } })();
      if (allowed.includes(role)) return mount(h);
      h.innerHTML = `<div class="page-content" style="padding:48px; text-align:center; color:var(--text-muted);">🔒 ${label} is limited to ${allowed.map(r => r.replace('_', ' ')).join(' / ')} roles.</div>`;
    };
    const ACCT = ['admin', 'director', 'manager', 'accounting'];
    this.pageInstances.set('accounting', new HubPage(this.contentContainer,
      { icon: '🧾', title: 'Accounting', purpose: 'The books-facing work in one place — supplies & maintenance expenses from QuickBooks, guest resolutions, Airbnb special-offer breakdowns, the per-property fees sheet, and the owner statement reviewer.' },
      [
        // returns the instance → the hub delegates suspendAutoRefresh (typed drafts survive the 15-min refresh)
        // R105 #6: roles hints drive the LANDING tab only (remote_ops now reaches this hub for Resolutions and must not land on a 🔒 tab); the roleGate mounts stay the enforcement
        { id: 'supexp', label: 'Supplies & Expenses', roles: ACCT, mount: async h => { const p = new SupExpPage(h); await p.render(); return p; } },
        { id: 'resolutions', label: 'Resolutions', roles: [...ACCT, 'remote_ops'], mount: roleGate([...ACCT, 'remote_ops'], 'Resolutions', h => new ResolutionsPage(h).render()) },
        { id: 'special-offers', label: 'Special Offers', roles: ACCT, mount: roleGate(ACCT, 'Special Offers', async h => { const { SpecialOffersPage } = await import('./pages/SpecialOffersPage'); const p = new SpecialOffersPage(h); await p.render(); return p; }) },
        { id: 'internal-expenses', label: 'Internal Expenses', roles: [...ACCT, 'remote_ops'], mount: roleGate([...ACCT, 'remote_ops'], 'Internal Expenses', async h => { const p = new InternalExpensesPage(h); await p.render(); return p; }) },
        { id: 'statements', label: 'Statement Reviewer', roles: ACCT, mount: roleGate(ACCT, 'The Statement Reviewer', h => new StatementsPage(h).render()) },
      ]));
    // Deep-link ids for the hub's tabs (search / app:navigate) — land on the hub with the tab preselected.
    this.pageInstances.set('resolutions', redirect('accounting', 'resolutions') as any);
    this.pageInstances.set('internal-expenses', redirect('accounting', 'internal-expenses') as any);
    this.pageInstances.set('special-offers', redirect('accounting', 'special-offers') as any);
  }

  private bindGlobalEvents(): void {
    // Programmatic navigation for pages (deep links to destinations that are
    // not in the sidebar, e.g. Triage → property workup). Pages dispatch:
    //   document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }))
    document.addEventListener('app:navigate', (e) => {
      const page = (e as CustomEvent).detail;
      if (typeof page === 'string' && page) this.navigate(page);
    });
    // R104 #10: activity tracking for the auto-refresh guard (passive, cheap).
    for (const ev of ['pointerdown', 'keydown', 'input', 'change', 'scroll', 'touchstart']) document.addEventListener(ev, () => this.noteActivity(), { passive: true, capture: true });

    const refreshBtn = document.getElementById('refresh-btn');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => this.refreshCurrentPage());
    }

    const hamburgerBtn = document.getElementById('hamburger-btn');
    if (hamburgerBtn) {
      hamburgerBtn.addEventListener('click', () => {
        document.body.classList.toggle('sidebar-mobile-open');
      });
    }

    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        document.body.classList.remove('sidebar-mobile-open');
      });
    }

    const globalMonth = document.getElementById('global-month');
    if (globalMonth) {
      globalMonth.addEventListener('change', () => this.onGlobalDateChange());
    }

    this._autoRefreshTimer = setInterval(() => this.autoRefresh(), 15 * 60 * 1000);
    this._relativeTimeTimer = setInterval(() => this.updateRelativeTime(), 30 * 1000);

    // Global search
    const searchBtn = document.getElementById('search-btn');
    const searchOverlay = document.getElementById('search-overlay') as HTMLElement;
    const searchInput = document.getElementById('search-input') as HTMLInputElement;
    const searchResults = document.getElementById('search-results') as HTMLElement;

    const openSearch = () => {
      searchOverlay.style.display = 'flex';
      setTimeout(() => searchInput?.focus(), 50);
    };
    (this as any)._openSearch = openSearch;
    const closeSearch = () => {
      searchOverlay.style.display = 'none';
      if (searchInput) searchInput.value = '';
      if (searchResults) searchResults.innerHTML = '<div style="padding:20px; text-align:center; color:#94a3b8; font-size:13px;">Type to search…</div>';
    };

    searchBtn?.addEventListener('click', openSearch);
    searchOverlay?.addEventListener('click', (e) => { if (e.target === searchOverlay) closeSearch(); });
    document.addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); openSearch(); }
      if (e.key === 'Escape') closeSearch();
    });

    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    const typeIcons: Record<string, string> = { listing: '🏠', reservation: '📅', ticket: '🎫' };
    const typeColors: Record<string, string> = { listing: '#3b82f6', reservation: '#0ea5e9', ticket: '#f59e0b' };

    searchInput?.addEventListener('input', () => {
      if (searchTimer) clearTimeout(searchTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) {
        searchResults.innerHTML = '<div style="padding:20px; text-align:center; color:#94a3b8; font-size:13px;">Type to search…</div>';
        return;
      }
      searchResults.innerHTML = '<div style="padding:20px; text-align:center; color:#94a3b8; font-size:13px;">Searching…</div>';
      searchTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`);
          const data = await res.json();
          if (!data.results?.length) {
            searchResults.innerHTML = '<div style="padding:20px; text-align:center; color:#94a3b8; font-size:13px;">No results found.</div>';
            return;
          }
          searchResults.innerHTML = data.results.map((r: any) => `
            <div class="search-result-row" data-page="${r.page}" data-id="${r.id}" style="display:flex; align-items:center; gap:12px; padding:10px 18px; cursor:pointer; transition:background 0.1s;">
              <span style="font-size:18px;">${typeIcons[r.type] || '📄'}</span>
              <div style="flex:1; min-width:0;">
                <div style="font-size:14px; font-weight:600; color:#1e293b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${r.title}</div>
                <div style="font-size:12px; color:#64748b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${r.sub}</div>
              </div>
              <span style="font-size:10px; font-weight:700; padding:2px 8px; border-radius:4px; background:${typeColors[r.type]}22; color:${typeColors[r.type]}; text-transform:uppercase; flex-shrink:0;">${r.type}</span>
            </div>`).join('');
          searchResults.querySelectorAll<HTMLElement>('.search-result-row').forEach(row => {
            row.addEventListener('mouseenter', () => row.style.background = 'var(--accent-soft)');
            row.addEventListener('mouseleave', () => row.style.background = '');
            row.addEventListener('click', () => {
              closeSearch();
              this.navigate(row.dataset.page!);
            });
          });
        } catch (_) {
          searchResults.innerHTML = '<div style="padding:20px; text-align:center; color:#94a3b8; font-size:13px;">Search failed.</div>';
        }
      }, 250);
    });
  }

  private onGlobalDateChange(): void {
    const val = (document.getElementById('global-month') as HTMLInputElement)?.value;
    if (!val) return;
    const [year, month] = val.split('-').map(Number);
    const from = new Date(year, month - 1, 1).toISOString().split('T')[0];
    const to = new Date(year, month, 0).toISOString().split('T')[0];
    (window as any).globalDateRange = { from, to, month: val };
    document.dispatchEvent(new CustomEvent('globalDateChange', { detail: { from, to, month: val } }));
    this.refreshCurrentPage();
  }

  // R104 #10 (Jesse: "the page will refresh automatically when I do not want it
  // to — a recurring problem all over"): beyond the per-page veto, the timer
  // never fires while someone is actively working — any click / keystroke /
  // edit in the last 5 minutes, or a text box holding focus, defers it.
  private _lastActivity = Date.now();
  private noteActivity(): void { this._lastActivity = Date.now(); }
  private userIsBusy(): boolean {
    if (Date.now() - this._lastActivity < 5 * 60 * 1000) return true;
    const a = document.activeElement as HTMLElement | null;
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) return true;
    return false;
  }
  private autoRefresh(): void {
    // R37: a page can veto the 15-min timer refresh while the user is mid-task —
    // render() wipes the DOM, which destroyed inspectors' capture/scoring screens
    // and cleaners' open job cards (picked-but-unsent photos included).
    const page = this.pageInstances.get(this.currentPage) as any;
    if (page && typeof page.suspendAutoRefresh === 'function' && page.suspendAutoRefresh()) return;
    if (this.userIsBusy()) return;   // R104 #10: nobody loses their place while they are typing / clicking
    this.refreshCurrentPage();
    this.lastRefreshTime = new Date();
  }

  private updateRelativeTime(): void {
    const lastUpdated = document.getElementById('last-updated');
    if (!lastUpdated) return;
    const diffMs = Date.now() - this.lastRefreshTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) {
      lastUpdated.textContent = 'Last updated just now';
    } else if (diffMins === 1) {
      lastUpdated.textContent = 'Last updated 1 min ago';
    } else {
      lastUpdated.textContent = `Last updated ${diffMins} mins ago`;
    }
  }

  async navigate(pageName: string): Promise<void> {
    if (pageName === this.currentPage) return;
    this.currentPage = pageName as PageName;
    document.body.classList.remove('sidebar-mobile-open');
    await this.loadPage(this.currentPage);
    this.updateActiveNav();
    this.updatePageTitle();
  }

  private async loadPage(pageName: PageName): Promise<void> {
    console.log('Loading page:', pageName);
    this.updatePageTitle(); // every load path, not just navigate() — boot and refresh too
    // R78: Admin + the metric glossary are office-only (Jesse: "should really
    // just be for admin/managers/directors/and remote staff"). The menu
    // entries are already hidden for field roles (TopNav/Sidebar); this covers
    // in-page links (e.g. Supplies' glossary link) and programmatic navigates.
    // The ⌘K search can't reach these — it only emits listing/reservation/
    // ticket results.
    if (pageName === 'admin' || pageName === 'glossary') {
      const role = (() => { try { return sessionStorage.getItem('userRole') || 'admin'; } catch { return 'admin'; } })();
      if (!['admin', 'director', 'manager', 'remote_ops'].includes(role)) {
        this.contentContainer.innerHTML = `<div class="page-content" style="padding:48px; text-align:center; color:var(--text-muted);">🔒 ${pageName === 'admin' ? 'Admin' : 'The metric glossary'} is limited to office roles.</div>`;
        return;
      }
    }
    const page = this.pageInstances.get(pageName);
    if (page && page.render) {
      await page.render();
    }
  }

  private async refreshCurrentPage(): Promise<void> {
    this.lastRefreshTime = new Date();
    const lastUpdated = document.getElementById('last-updated');
    if (lastUpdated) {
      lastUpdated.textContent = 'Last updated just now';
    }
    await this.loadPage(this.currentPage);
  }

  private updateActiveNav(): void {
    // Sidebar holds the page nav — re-render it to move the active highlight.
    this.sidebar.setActive(this.currentPage);
    const sbEl = document.getElementById('app-sidebar');
    if (sbEl) { sbEl.outerHTML = this.sidebar.render(); this.sidebar.bindEvents(); }
    this.nav.setActive(this.currentPage);
    const navEl = document.querySelector('.topnav');
    if (navEl) {
      // Preserve the live month value across the re-render.
      const month = (document.getElementById('global-month') as HTMLInputElement | null)?.value;
      navEl.outerHTML = this.nav.render();
      if (month) {
        const m2 = document.getElementById('global-month') as HTMLInputElement | null;
        if (m2) m2.value = month;
      }
      this.nav.bindEvents();
      this.bindNavControls();
    }
  }

  // The right-cluster controls live inside the nav and rebind on each render.
  private bindNavControls(): void {
    document.getElementById('refresh-btn')?.addEventListener('click', () => this.refreshCurrentPage());
    document.getElementById('global-month')?.addEventListener('change', () => this.onGlobalDateChange());
    document.getElementById('search-btn')?.addEventListener('click', () => (this as any)._openSearch?.());
  }

  private updatePageTitle(): void {
    const titles: Record<PageName, string> = {
      today: 'Today',
      triage: 'Priority Queue',
      money: 'Analytics',
      'hk-insp': 'Housekeeping & Inspections',
      pnl: 'P&L Review',
      'gap-nights': 'Gap Nights',
      'event-pricing': 'Event Pricing',
      projections: 'Revenue Projections',
      profitability: 'Profitability Analysis',
      portfolio: 'Portfolio',
      listings: 'All Listings',
      reservations: 'Reservations',
      reviews: 'Reviews',
      operations: 'Tasks & Expenses',
      settings: 'Configuration',
      'booking-quote': 'Booking Quote',
      'data-explorer': 'AI Revenue Manager',
      impact: 'Impact',
      reports: 'Portfolio Report',
      glossary: 'Glossary',
      owners: 'Owners & Properties',
      housekeeping: 'Housekeeping',
      issues: 'Issues',
      analytics: 'Analytics',
      users: 'Users & Roles',
      'owner-portal': 'Owner Portal',
      'owner-profile': 'Owner Profile',
      sops: 'SOP Library',
      jarvis: 'Jarvis',
      ffe: 'FF&E / Housewares',
      'field-ops': 'Field Operations',
      'owner-ops': 'Owner Operations',
      tasks: 'Tasks',
      'tasks-hub': 'Tasks',   // R111: compatibility id — forwards to Tasks / Issues / Guest Recovery / Remote Operations
      'guest-recovery': 'Guest Recovery',
      claims: 'Damages & Claims',
      'remote-ops': 'Remote Operations',
      arrivals: 'Arrivals',
      'guest-texts': 'Messaging',   // R112: the universal inbox (id kept — Calendar's handoff and 'qx_*' keys target it)
      messaging: 'Messaging',
      stays: 'Calendar',
      supexp: 'Supplies & Expenses',
      accounting: 'Accounting',
      resolutions: 'Resolutions',
      'internal-expenses': 'Internal Expenses',
      'special-offers': 'Special Offers',
    };
    const titleEl = document.getElementById('page-title');
    if (titleEl) {
      titleEl.textContent = titles[this.currentPage] || this.currentPage;
    }
  }
}
